/* eslint-disable jsx-a11y/anchor-is-valid */
import {MutableRefObject, createRef, useEffect, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {getUserByToken, login} from '../core/_requests'
import {useAuth} from '../core/Auth'
import Cookies from 'universal-cookie'
import ReCAPTCHA from 'react-google-recaptcha'
import { onGetUserRoles } from '../../base-definitions/components/roles/core/_request'
import { Card } from 'antd'
import { IntlProvider, useIntl } from 'react-intl'

import "./css/style.css"
import { TwoWayAuth } from './TwoWayAuth'
import { getTenantConfig } from '../../../configs/tenantconfig'


const initialValues = {
  userNameOrEmailAddress: '',
  password: '',
  recaptchaToken: '',
}


const flagCss :  React.CSSProperties = {width: '2.5rem', aspectRatio : '3/2', overflow: 'hidden', objectFit: 'cover', objectPosition: 'center', cursor : 'pointer'};
/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function Login({ setLocale }) {
  const intl = useIntl()

  const [loading, setLoading] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()
  const [twoWayAuthRequired, setTwoWayAuthRequired] = useState<boolean>(false);

  let captcha;
  const setCaptchaRef = (ref) => {
    if (ref) {
      return captcha = ref;
    }
 };
 const { name, logo, hr_info } = getTenantConfig();
 const tenantName = name || "Default University";
 const tenantLogo = logo || "default-logo.png";

 const loginSchema = Yup.object().shape({
  userNameOrEmailAddress: Yup.string()
    .min(3, intl.formatMessage({id: 'AUTH.ERROR.MIN3'}))
    .max(50, intl.formatMessage({id: 'AUTH.ERROR.MAX50'}))
    .required(intl.formatMessage({id: 'AUTH.EMAIL.REQUIRED'})),
  password: Yup.string()
    .min(3, intl.formatMessage({id: 'AUTH.ERROR.MIN3'}))
    .max(50, intl.formatMessage({id: 'AUTH.ERROR.MAX50'}))
    .required(intl.formatMessage({id: 'AUTH.PASSWORD.REQUIRED'})),
  recaptchaToken: Yup.string().required(intl.formatMessage({id: 'AUTH.RECAPTCHA.REQUIRED'})),
})

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const {data: auth} = await login(
          values.userNameOrEmailAddress,
          values.password,
          values.recaptchaToken,
          intl.locale ?? "tr-TR"
        )
        
        if(auth.result.is2FactorAuthEnabled)
        {
          setTwoWayAuthRequired(true)
        }
        else
        {
          saveAuth(auth)
          const cookies = new Cookies()
          cookies.set('enc_auth_token', auth.result.accessToken, 
            {
              path: '/',
            });
          const { data: user } = await getUserByToken();
          const { data : permissions } = await onGetUserRoles();
          user.result.permissions = permissions.result; 
          setCurrentUser(user)
        }

      } catch (error) {
        saveAuth(undefined)
        setStatus(`${(error as any).response.data.error.message}`)
        setSubmitting(false)
        setLoading(false)

        captcha.reset();
        formik.setFieldValue('recaptchaToken', '')
      }
    },
  })

  const handleRecaptchaChange = (e: any) => {
    // setFieldValue('recaptcha', value)
    formik.setFieldValue('recaptchaToken', e)
  }
  

  return (
    <>
      <div style={{ display : twoWayAuthRequired ? "none": "block"}}>
        <form
          className='form w-100'
          onSubmit={formik.handleSubmit}
          noValidate
          id='kt_login_signin_form'
        >
        <Card className="card-container card-bottom-padding shadow-lg">
          <div className="flags" style={{  position: 'absolute', top: '0.5rem', right: '0.5rem',  display: 'flex', gap: '5px', zIndex: '10'}}>
            <img className="rounded-1" src="/media/flags/turkey.svg" alt="Turkish" onClick={() =>  setLocale('tr')}
              style={flagCss} />
            <img className="rounded-1" src="/media/flags/united-states.svg" alt="English"  onClick={() => setLocale('en')}
              style={flagCss} />
          </div>
          <div style={{justifyContent: 'center', display: 'flex', paddingTop : '0rem', overflow : 'visible' }}>
            <img className="logo" src={`/media/logos/uni/${tenantLogo}`} alt="University Logo" 
              style={{maxWidth : '80%', height : '12rem', objectPosition: 'center', justifyContent: 'center', display: 'flex'}} />
          </div>
            {/* begin::Heading */}
            <div className='text-center mb-5 mt-5'>
              <h1 className=' fw-bolder title-with-shadow'>{intl.formatMessage({id: 'GENERAL.APPNAME'})}</h1>
              <h4 className='text-dark fw-bolder '>{intl.formatMessage({id: 'AUTH.LOGIN'})}</h4>
            </div>
            {/* begin::Heading */}
            {
              formik.status && (
                <div className='mb-lg-15 alert alert-danger'>
                  <div className='alert-text font-weight-bold'>{formik.status}</div>
                </div>
              )
            }

            {/* begin::Form group */}
            <div className='fv-row mb-8'>
              <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({id: 'AUTH.EMAIL'})}</label>
              <input
                placeholder={intl.formatMessage({id: 'AUTH.EMAIL.PLACEHOLDER'})}
                {...formik.getFieldProps('email')}
                className={clsx(
                  'form-control bg-transparent',
                  {
                    'is-invalid':
                      formik.touched.userNameOrEmailAddress && formik.errors.userNameOrEmailAddress,
                  },
                  {
                    'is-valid':
                      formik.touched.userNameOrEmailAddress && !formik.errors.userNameOrEmailAddress,
                  }
                )}
                type='text'
                name='userNameOrEmailAddress'
                autoComplete='off'
              />
              {formik.touched.userNameOrEmailAddress && formik.errors.userNameOrEmailAddress && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.userNameOrEmailAddress}</span>
                  </div>
                </div>
              )}
            </div>
            {/* end::Form group */}

            {/* begin::Form group */}
            <div className='fv-row mb-3'>
              <label className='form-label fw-bolder text-dark fs-6 mb-0'>{intl.formatMessage({id: 'AUTH.PASSWORD'})}</label>
              <input
                type='password'
                autoComplete='off'
                {...formik.getFieldProps('password')}
                className={clsx(
                  'form-control bg-transparent',
                  {
                    'is-invalid': formik.touched.password && formik.errors.password,
                  },
                  {
                    'is-valid': formik.touched.password && !formik.errors.password,
                  }
                )}
              />
              {formik.touched.password && formik.errors.password && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.password}</span>
                  </div>
                </div>
              )}
            </div>
            <div className='fv-row mb-3'>
              <ReCAPTCHA
                ref={(r) => setCaptchaRef(r)}
                {...formik.getFieldProps('recaptchaToken')}
                sitekey={process.env.REACT_APP_RECAPTCHA2_SITE_KEY}
                onChange={handleRecaptchaChange}
                hl={intl.locale}
              />
              {formik.touched.recaptchaToken && formik.errors.recaptchaToken && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.recaptchaToken}</span>
                  </div>
                </div>
              )}
            </div>
            <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
              <div />

              {/* begin::Link */}
              <Link to='/auth/forgot-password' className='link-primary'>
              {intl.formatMessage({id: 'AUTH.FORGOTPASSWORD'})}
              </Link>
              {/* end::Link */}
            </div>
            <div className='d-grid mb-10'>
              <button
                type='submit'
                id='kt_sign_in_submit'
                className='btn btn-primary'
                disabled={formik.isSubmitting || !formik.isValid}
              >
                {!loading && <span className='indicator-label'>{intl.formatMessage({id: 'AUTH.LOGIN.BUTTON'})}</span>}
                {loading && (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    {intl.formatMessage({id: 'AUTH.PLEASEWAIT'})}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
            </div>
            {/* end::Action */}

            <div className='text-gray-500 text-center fw-semibold fs-6'>
            {intl.formatMessage({id: 'AUTH.NOTAMEMBER'})}{' '}
              <Link to='/auth/registration' className='link-primary'>
              {intl.formatMessage({id: 'AUTH.SIGNUP'})}
              </Link>
            </div>
            {hr_info && (
              <div
                className='text-gray-700 text-center fw-semibold fs-6 py-2 my-2'
                style={{ border: "1px solid #bfbfbf", backgroundColor: "#f8f8f8", borderRadius: "9px" }}>
                {intl.formatMessage({ id: 'AUTH.INFO_FOR_HR' })}
              </div>)}
            <div className="custom-card-footer">{intl.formatMessage({id: 'AUTH.DEVELOPEDBYUNIPA'})}</div>
          </Card>
        </form>
        </div>
        <div style={{ display : twoWayAuthRequired ? "block": "none"}}>
              <TwoWayAuth 
                username={formik.values.userNameOrEmailAddress}
                password={formik.values.password}
              ></TwoWayAuth>
        </div>
    </>
  )
}
